import React, { useEffect, useMemo, useState, useCallback } from 'react';
import './ParentsPage.css';
import { getImageUrl } from './utils/mediaUtils';
import { getAPIBase } from './config';
import {
  ArrowRight,
  Users,
  Calendar,
  Award,
  BookOpen,
  Star,
  CheckCircle2,
  Download,
  FileText,
  Heart,
  Shield,
  Phone,
  BarChart3,
  TrendingUp,
  ShieldCheck,
  Smartphone,
  Laptop,
  ExternalLink,
  Lock,
  GraduationCap,
  Mail,
  Layout,
  MessageSquare,
} from 'lucide-react';

const API_BASE = (() => {
  if (process.env.REACT_APP_API_BASE) return process.env.REACT_APP_API_BASE;
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined') {
    const { hostname, protocol } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://permataharapansecondary.sch.id/api';
    }
    return `${protocol}//${hostname}/api`;
  }
  return 'https://permataharapansecondary.sch.id/api';
})();

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return (
    lower.endsWith('.mp4') ||
    lower.endsWith('.webm') ||
    lower.endsWith('.ogg') ||
    lower.includes('youtube.com') ||
    lower.includes('youtu.be')
  );
};

const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return m ? m[1] : null;
};

function ParentsPage({ page, onNavigate }) {
  // 1. Media Map from CMS
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_homepage') || sessionStorage.getItem('cms_media_map');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  // 2. Published sections map directly from content_published
  const [liveSections, setLiveSections] = useState(() => {
    if (page?.sections && Object.keys(page.sections).length > 0) {
      return page.sections;
    }
    try {
      const cached = sessionStorage.getItem('cms_page_parents');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });

  const [loading, setLoading] = useState(!liveSections);

  // Load CMS media map
  useEffect(() => {
    let isMounted = true;
    const loadMedia = async () => {
      try {
        const apiBase = getAPIBase ? getAPIBase() : API_BASE;
        const res = await fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
          credentials: 'omit',
        });
        if (!res.ok) return;
        const json = await res.json();
        if (json.ok && Array.isArray(json.data)) {
          const map = {};
          json.data.forEach((m) => {
            const mId = m.id || m.media_id;
            let mUrl = m.url || m.file_path;
            if (mUrl && !mUrl.startsWith('http://') && !mUrl.startsWith('https://')) {
              mUrl = `${API_ORIGIN}${mUrl.startsWith('/') ? '' : '/'}${mUrl}`;
            }
            if (mId && mUrl) {
              map[mId] = mUrl;
              map[String(mId)] = mUrl;
              const num = Number(mId);
              if (!isNaN(num)) map[num] = mUrl;
            }
          });
          if (isMounted && Object.keys(map).length > 0) {
            setMediaMap(map);
            try {
              sessionStorage.setItem('cms_media_homepage', JSON.stringify(map));
            } catch (_) {}
          }
        }
      } catch (e) {
        console.warn('[ParentsPage] Media fetch error:', e);
      }
    };
    loadMedia();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch published sections directly from database (content_published)
  const fetchPublishedSections = useCallback(async () => {
    try {
      const apiBase = getAPIBase ? getAPIBase() : API_BASE;
      const res = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=parents&_t=${Date.now()}`, {
        credentials: 'omit',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
        },
      });
      if (res.ok) {
        const json = await res.json();
        const sections = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        if (sections.length > 0) {
          const sectionMap = {};
          sections.forEach((s) => {
            const cp =
              s.content_published && s.content_published !== '{}' && s.content_published !== '[]'
                ? s.content_published
                : s.content || s.content_published;
            let parsed = null;
            try {
              parsed = typeof cp === 'string' ? JSON.parse(cp) : cp;
            } catch (e) {
              parsed = cp;
            }
            if (parsed) {
              sectionMap[s.section_type] = parsed.en || parsed;
            }
          });
          if (Object.keys(sectionMap).length > 0) {
            setLiveSections(sectionMap);
            try {
              sessionStorage.setItem('cms_page_parents', JSON.stringify(sectionMap));
            } catch (_) {}
          }
        }
      }
    } catch (err) {
      console.warn('[ParentsPage] Error fetching published parents sections:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPublishedSections();

    const handleUpdate = () => {
      fetchPublishedSections();
    };
    window.addEventListener('approvalCompleted', handleUpdate);
    window.addEventListener('cmsContentUpdated', handleUpdate);

    return () => {
      window.removeEventListener('approvalCompleted', handleUpdate);
      window.removeEventListener('cmsContentUpdated', handleUpdate);
    };
  }, [fetchPublishedSections]);

  // Sync if page prop updates
  useEffect(() => {
    if (page?.sections && Object.keys(page.sections).length > 0) {
      setLiveSections((prev) => (prev ? { ...page.sections, ...prev } : page.sections));
    }
  }, [page?.sections]);

  // Pure dynamic media resolver - strictly zero hardcoded image fallbacks
  const resolveMedia = useCallback(
    (src) => {
      if (!src) return '';
      if (typeof src === 'object' && src !== null) {
        src = src.image || src.url || src.file_path || src.src || '';
      }
      const str = String(src).trim();
      if (!str) return '';
      if (
        str.startsWith('http://') ||
        str.startsWith('https://') ||
        str.startsWith('blob:') ||
        str.startsWith('data:')
      ) {
        return str;
      }
      if (mediaMap) {
        if (mediaMap[str]) return mediaMap[str];
        const num = Number(str);
        if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
      }
      const resolved = getImageUrl ? getImageUrl(str, mediaMap) : '';
      if (resolved) {
        if (
          resolved.startsWith('http://') ||
          resolved.startsWith('https://') ||
          resolved.startsWith('blob:') ||
          resolved.startsWith('data:')
        ) {
          return resolved;
        }
        return `${API_ORIGIN}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
      }
      if (str.startsWith('/')) {
        return `${API_ORIGIN}${str}`;
      }
      if (/^\d+$/.test(str)) return '';
      return `${API_ORIGIN}/uploads/images/${str}`;
    },
    [mediaMap]
  );

  // Safe external URL formatter
  const formatSafeUrl = useCallback((url, fallback = '#') => {
    if (!url) return fallback;
    const trimmed = String(url).trim();
    if (!trimmed || trimmed === '#') return fallback;
    if (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('mailto:') ||
      trimmed.startsWith('tel:') ||
      trimmed.startsWith('/')
    ) {
      return trimmed;
    }
    return `https://${trimmed}`;
  }, []);

  // 1. HERO SECTION DATA
  const heroSec = liveSections?.['parents-hero'] || page?.sections?.['parents-hero'] || {};
  const heroKicker = heroSec?.kicker || page?.kicker || '';
  const heroTitle = heroSec?.title || page?.title || '';
  const heroSummary = heroSec?.summary || heroSec?.subtitle || page?.summary || page?.subtitle || '';

  const heroSlides = useMemo(() => {
    const rawSlides = heroSec?.slides || heroSec?.hero_slides || page?.hero_slides || page?.slides || [];
    if (!Array.isArray(rawSlides)) return [];

    return rawSlides
      .map((slide, idx) => {
        const rawUrl = typeof slide === 'object' && slide !== null
          ? (slide.image || slide.url || slide.file_path || slide.src)
          : slide;
        const url = resolveMedia(rawUrl);
        const isVideo = isVideoUrl(url);
        const youtubeId = isVideo ? extractYouTubeId(url) : null;
        return {
          id: slide?.id || idx,
          url,
          caption: slide?.caption || '',
          isVideo,
          isYouTube: Boolean(youtubeId),
          youtubeId,
        };
      })
      .filter((s) => Boolean(s.url));
  }, [heroSec?.slides, heroSec?.hero_slides, page?.hero_slides, page?.slides, resolveMedia]);

  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    if (!heroSlides.length) return;
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  // 2. PARENTS LEARNING COMMUNITY (PaLC) DATA
  const palcSec = liveSections?.['parents-palc'] || page?.sections?.['parents-palc'] || {};
  const palcKicker = palcSec?.kicker || page?.palc_kicker || '';
  const palcTitle = palcSec?.title || page?.palc_title || '';
  const palcSubtitle = palcSec?.subtitle || palcSec?.description || page?.palc_subtitle || '';
  const palcStats = palcSec?.stats || page?.palc_stats || null;

  const palc = useMemo(() => {
    const rawPalc = palcSec?.items || palcSec?.parents_learning_community || page?.parents_learning_community || [];
    if (!Array.isArray(rawPalc)) return [];

    return rawPalc.map((item, index) => {
      let itemImg = item?.image;
      if (!itemImg && Array.isArray(item?.images) && item.images.length > 0) {
        itemImg = item.images.find(Boolean);
      }
      const resolvedImage = resolveMedia(itemImg);
      let benefitsList = item?.benefits || [];
      if (typeof benefitsList === 'string') {
        benefitsList = benefitsList.split(',').map((s) => s.trim()).filter(Boolean);
      }
      return {
        ...item,
        id: item?.id || `palc-${index + 1}`,
        title: item?.title || '',
        description: item?.description || '',
        image: resolvedImage,
        benefits: benefitsList,
      };
    });
  }, [palcSec?.items, palcSec?.parents_learning_community, page?.parents_learning_community, resolveMedia]);

  const palcBenefits = useMemo(() => {
    const raw = palcSec?.benefits || page?.palc_benefits || [];
    return Array.isArray(raw) ? raw : [];
  }, [palcSec?.benefits, page?.palc_benefits]);

  const handleNavigateToPalc = useCallback((item) => {
    if (!onNavigate) return;
    const rawId = item?.id || '';
    const cleanId = String(rawId).replace(/^palc-/, '');
    onNavigate(`palc-${cleanId}`);
  }, [onNavigate]);

  // 3. HANDBOOK DATA
  const handbookSec = liveSections?.['parents-handbook'] || page?.sections?.['parents-handbook'] || {};
  const handbookKicker = handbookSec?.kicker || page?.handbook_kicker || '';
  const handbookTitle = handbookSec?.title || page?.handbook_title || '';
  const handbookSubtitle = handbookSec?.subtitle || page?.handbook_subtitle || '';
  const handbookData = handbookSec?.handbook || page?.handbook || {};
  const handbookCoverUrl = handbookData?.image ? resolveMedia(handbookData.image) : '';

  const resolvedDownloadUrl = useMemo(() => {
    if (!handbookData?.download_url) return '#';
    let url = String(handbookData.download_url).trim();
    if (!url || url === '#') return '#';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
      return url;
    }
    return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
  }, [handbookData?.download_url]);

  const insideTopics = useMemo(() => {
    const raw = handbookSec?.inside_topics || page?.inside_topics || [];
    return Array.isArray(raw) ? raw : [];
  }, [handbookSec?.inside_topics, page?.inside_topics]);

  // 4. PORTAL ACCESS DATA
  const portalSec = liveSections?.['parents-portal'] || page?.sections?.['parents-portal'] || {};
  const portalKicker = portalSec?.kicker || page?.portal_kicker || '';
  const portalTitle = portalSec?.title || page?.portal_title || '';
  const portalSubtitle = portalSec?.subtitle || page?.portal_subtitle || '';
  const portalFeatures = Array.isArray(portalSec?.features) ? portalSec.features : (Array.isArray(page?.portal_features) ? page.portal_features : []);
  const portalCta = portalSec?.cta || page?.portal_cta || {};
  const portalButtonUrl = formatSafeUrl(portalCta?.button_url, 'https://classroom.google.com');

  // 5. COMMUNICATION CHANNELS DATA
  const commSec = liveSections?.['parents-communication'] || page?.sections?.['parents-communication'] || {};
  const commKicker = commSec?.kicker || page?.communication_kicker || '';
  const commTitle = commSec?.title || page?.communication_title || '';
  const commSubtitle = commSec?.subtitle || page?.communication_subtitle || '';
  const communicationChannels = Array.isArray(commSec?.channels) ? commSec.channels : (Array.isArray(page?.communication_channels) ? page.communication_channels : []);

  // Scroll animations
  useEffect(() => {
    const animatedItems = document.querySelectorAll('.parents-animate');
    if (!animatedItems.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    animatedItems.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, [palc, insideTopics, communicationChannels]);

  if (loading && !liveSections) {
    return (
      <section className="parents-page" style={{ padding: '60px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div
          style={{
            height: '380px',
            borderRadius: '24px',
            background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
            backgroundSize: '200% 100%',
            animation: 'pulse 1.5s infinite',
            marginBottom: '48px',
          }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
          <div style={{ height: '240px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
          <div style={{ height: '240px', borderRadius: '16px', background: '#e2e8f0', animation: 'pulse 1.5s infinite' }} />
        </div>
      </section>
    );
  }

  return (
    <section className="parents-page">
      {/* 1. HERO SECTION (Identical to About Us Hero) */}
      <section className="parents-hero" id="parents-overview">
        <div className="parents-hero-media" aria-hidden="true">
          {heroSlides.length > 0 ? (
            heroSlides.map((slide, index) => {
              const isCurrent = index === activeSlide;
              return (
                <div
                  key={slide.id || index}
                  className={`parents-hero-slide ${isCurrent ? 'active' : ''}`}
                >
                  {slide.isVideo ? (
                    slide.isYouTube ? (
                      <iframe
                        className="parents-hero-video-frame"
                        src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${slide.youtubeId}&controls=0&playsinline=1&modestbranding=1&rel=0`}
                        title="Parents Hero video background"
                        allow="autoplay; encrypted-media"
                        tabIndex="-1"
                      />
                    ) : (
                      <video
                        className="parents-hero-video-element"
                        src={slide.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    )
                  ) : (
                    <img
                      src={slide.url}
                      alt={heroTitle || 'Parents'}
                      className="parents-hero-img-element"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="parents-hero-slide active parents-hero-gradient-fallback" />
          )}
        </div>

        <div className="parents-hero-overlay" aria-hidden="true" />

        <div className="parents-hero-content-wrap">
          <div className="parents-hero-content">
            {heroKicker && (
              <p className="parents-hero-kicker">
                {heroKicker}
              </p>
            )}

            {heroTitle && (
              <h1 className="parents-hero-title">
                {heroTitle}
              </h1>
            )}

            {heroSummary && (
              <p className="parents-hero-summary" style={{ whiteSpace: 'pre-line' }}>
                {heroSummary}
              </p>
            )}

            {heroSlides.length > 1 && (
              <div className="parents-hero-dots" role="tablist" aria-label="Parents hero carousel navigation">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.id || index}
                    type="button"
                    className={`parents-hero-dot ${index === activeSlide ? 'active' : ''}`}
                    onClick={() => setActiveSlide(index)}
                    aria-label={`Show slide ${index + 1}`}
                    role="tab"
                    aria-selected={index === activeSlide}
                  >
                    <span className="parents-hero-dot-bar" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 2. PARENTS LEARNING COMMUNITY (PaLC) - ROYAL PURPLE & GOLD */}
      {(palc.length > 0 || palcTitle) && (
        <section className="parents-palc" id="parents-learning-community">
          <div className="parents-palc-container">
            {/* Header & Stats Block */}
            <div className="parents-palc-header parents-animate">
              <div className="parents-palc-header-content">
                {palcKicker && (
                  <div className="palc-kicker-badge">
                    <span className="palc-kicker-dot" />
                    <span>{palcKicker}</span>
                  </div>
                )}
                {palcTitle && <h2 className="palc-title">{palcTitle}</h2>}
                {palcSubtitle && <p className="palc-subtitle" style={{ whiteSpace: 'pre-line' }}>{palcSubtitle}</p>}
              </div>

              {palcStats && (
                <div className="parents-palc-stats">
                  {palcStats.active_parents_count && (
                    <div className="palc-stat">
                      <div className="palc-stat-number">{palcStats.active_parents_count}</div>
                      <div className="palc-stat-label">{palcStats.active_parents_label || 'Active Parents'}</div>
                    </div>
                  )}
                  {palcStats.annual_events_count && (
                    <div className="palc-stat">
                      <div className="palc-stat-number">{palcStats.annual_events_count}</div>
                      <div className="palc-stat-label">{palcStats.annual_events_label || 'Annual Events'}</div>
                    </div>
                  )}
                  {palcStats.learning_programs_count && (
                    <div className="palc-stat">
                      <div className="palc-stat-number">{palcStats.learning_programs_count}</div>
                      <div className="palc-stat-label">{palcStats.learning_programs_label || 'Learning Programs'}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Programs Showcase Grid */}
            {palc.length > 0 && (
              <div className="parents-palc-programs-grid">
                {palc.map((item, index) => (
                  <article
                    key={item.id}
                    id={item.id}
                    className="palc-card parents-animate"
                    onClick={() => handleNavigateToPalc(item)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleNavigateToPalc(item);
                      }
                    }}
                  >
                    <div className="palc-card-accent-bar" />
                    <div className="palc-card-media">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.title}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="palc-card-media-fallback" />
                      )}
                      <div className="palc-card-overlay" />
                      <div className="palc-card-meta-tags">
                        <span className="palc-tag-frequency">
                          <Calendar size={12} />
                          <span>{item.date || item.frequency || 'Community'}</span>
                        </span>
                        <span className="palc-tag-index">
                          {item.is_featured ? 'Signature' : index + 1 < 10 ? `0${index + 1}` : index + 1}
                        </span>
                      </div>
                    </div>

                    <div className="palc-card-body">
                      {item.title && <h3 className="palc-card-title">{item.title}</h3>}
                      {item.description && <p className="palc-card-desc" style={{ whiteSpace: 'pre-line' }}>{item.description}</p>}

                      {Array.isArray(item.benefits) && item.benefits.length > 0 && (
                        <div className="palc-card-benefits">
                          {item.benefits.map((b, bIdx) => (
                            <span key={bIdx} className="palc-benefit-pill">
                              <CheckCircle2 size={12} />
                              <span>{b}</span>
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="palc-card-footer">
                        <button type="button" className="palc-card-link">
                          <span>Explore Program</span>
                          <ArrowRight size={15} />
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}

            {/* Why Join PaLC Benefits Card */}
            {palcBenefits.length > 0 && (
              <div className="parents-palc-benefits-card parents-animate">
                <h3 className="palc-benefits-title">Why Join PaLC?</h3>
                <div className="palc-benefits-grid">
                  {palcBenefits.map((benefit, bIdx) => {
                    const benefitIcons = [
                      <BookOpen size={22} />,
                      <Users size={22} />,
                      <Award size={22} />,
                      <Star size={22} />,
                    ];
                    const icon = benefitIcons[bIdx % benefitIcons.length];
                    return (
                      <div key={bIdx} className="palc-benefit-box">
                        <div className="palc-benefit-icon-wrap">{icon}</div>
                        <h4 className="palc-benefit-title">{benefit.title}</h4>
                        <p className="palc-benefit-desc" style={{ whiteSpace: 'pre-line' }}>{benefit.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 3. PARENT & STUDENT HANDBOOK */}
      {(handbookTitle || handbookData.title) && (
        <section className="handbook-section" id="parent-student-handbook">
          <div className="handbook-wrapper">
            {/* Header */}
            <div className="handbook-header-block parents-animate">
              {handbookKicker && (
                <div className="handbook-kicker-badge">
                  <span className="handbook-kicker-dot" />
                  <span>{handbookKicker}</span>
                </div>
              )}
              {handbookTitle && <h2 className="handbook-section-title">{handbookTitle}</h2>}
              {handbookSubtitle && <p className="handbook-section-subtitle" style={{ whiteSpace: 'pre-line' }}>{handbookSubtitle}</p>}
            </div>

            {/* Main Featured Card */}
            <div className="handbook-featured-card parents-animate">
              <div className="handbook-left-column">
                <div className="handbook-featured-image">
                  {handbookCoverUrl && (
                    <img
                      src={handbookCoverUrl}
                      alt={handbookData.title || 'Parent & Student Handbook'}
                      className="handbook-image"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                  <div className="handbook-image-overlay" />
                  <div className="handbook-pdf-badge">
                    <FileText size={14} />
                    <span>PDF GUIDE</span>
                  </div>
                </div>
              </div>

              <div className="handbook-featured-content">
                {handbookData.label && <p className="handbook-featured-label">{handbookData.label}</p>}
                <h3 className="handbook-featured-title">{handbookData.title || handbookTitle}</h3>
                {handbookData.description && <p className="handbook-featured-description" style={{ whiteSpace: 'pre-line' }}>{handbookData.description}</p>}

                {/* Key Features */}
                {Array.isArray(handbookData.features) && handbookData.features.length > 0 && (
                  <div className="handbook-key-features">
                    {handbookData.features.map((feat, fIdx) => (
                      <div key={fIdx} className="key-feature">
                        <CheckCircle2 size={16} />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* File Info */}
                <div className="handbook-file-details">
                  {handbookData.file_size && (
                    <span>
                      <FileText size={15} />
                      <span>{handbookData.file_size}</span>
                    </span>
                  )}
                  {handbookData.file_pages && (
                    <span>
                      <BookOpen size={15} />
                      <span>{handbookData.file_pages}</span>
                    </span>
                  )}
                  {handbookData.file_date && (
                    <span>
                      <Calendar size={15} />
                      <span>{handbookData.file_date}</span>
                    </span>
                  )}
                </div>

                {/* Download Button */}
                <a
                  href={resolvedDownloadUrl}
                  download="Parent_and_Student_Handbook.pdf"
                  className="handbook-download-cta"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (resolvedDownloadUrl === '#') {
                      e.preventDefault();
                      alert('Handbook file is currently being prepared. Please check back soon.');
                    }
                  }}
                >
                  <Download size={18} />
                  <span>Download Handbook</span>
                </a>
              </div>
            </div>

            {/* What's Inside Grid */}
            {insideTopics.length > 0 && (
              <div className="handbook-whats-inside parents-animate">
                <h3 className="whats-inside-title">What's Inside</h3>
                <div className="whats-inside-grid">
                  {insideTopics.map((topic, tIdx) => {
                    const topicLucideIcons = [
                      <BookOpen size={24} />,
                      <Calendar size={24} />,
                      <BarChart3 size={24} />,
                      <Heart size={24} />,
                      <Shield size={24} />,
                      <Phone size={24} />
                    ];
                    const icon = topicLucideIcons[tIdx % topicLucideIcons.length];
                    return (
                      <div key={tIdx} className="inside-item">
                        <div className="inside-icon">{icon}</div>
                        <h4>{topic.title}</h4>
                        <p style={{ whiteSpace: 'pre-line' }}>{topic.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* 4. PARENT PORTAL ACCESS */}
      {(portalTitle || portalFeatures.length > 0) && (
        <section className="parents-portal" id="parent-portal-access">
          <div className="portal-wrapper">
            <div className="portal-header parents-animate">
              {portalKicker && (
                <div className="portal-kicker-badge">
                  <span className="portal-kicker-dot" />
                  <span>{portalKicker}</span>
                </div>
              )}
              {portalTitle && <h2 className="portal-title">{portalTitle}</h2>}
              {portalSubtitle && <p className="portal-subtitle" style={{ whiteSpace: 'pre-line' }}>{portalSubtitle}</p>}
            </div>

            <div className="portal-main-grid parents-animate">
              {/* Left Column: Feature Cards Grid */}
              {portalFeatures.length > 0 && (
                <div className="portal-features-grid">
                  {portalFeatures.map((feat, fIdx) => {
                    const featureIcons = [
                      <TrendingUp size={22} />,
                      <Calendar size={22} />,
                      <ShieldCheck size={22} />,
                      <Smartphone size={22} />,
                    ];
                    const icon = featureIcons[fIdx % featureIcons.length];
                    return (
                      <div key={fIdx} className="portal-feature-card">
                        <div className="portal-feature-icon-wrap">{icon}</div>
                        <div className="portal-feature-text">
                          <h4 className="portal-feature-title">{feat.title}</h4>
                          <p className="portal-feature-desc" style={{ whiteSpace: 'pre-line' }}>{feat.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Right Column: Portal CTA Showcase Box */}
              <div className="portal-cta-showcase">
                <div className="portal-cta-top-accent" />
                <div className="portal-cta-badge">
                  <span className="portal-status-dot" />
                  <span>Secure LMS Gateway</span>
                </div>

                <div className="portal-cta-icon-box">
                  <Laptop size={36} />
                </div>

                <h3 className="portal-cta-heading">{portalCta.title || 'Access LMS Portal'}</h3>
                {portalCta.description && <p className="portal-cta-copy" style={{ whiteSpace: 'pre-line' }}>{portalCta.description}</p>}

                <a
                  href={portalButtonUrl}
                  className="portal-cta-action-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{portalCta.button_label || 'Launch Portal'}</span>
                  <ExternalLink size={18} />
                </a>

                {portalCta.note && (
                  <div className="portal-cta-security-note">
                    <Lock size={13} />
                    <span>{portalCta.note}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 5. COMMUNICATION CHANNELS */}
      {(commTitle || communicationChannels.length > 0) && (
        <section className="parents-communication" id="communication-channels">
          <div className="communication-wrapper">
            {/* Header */}
            <div className="communication-header parents-animate">
              {commKicker && (
                <div className="communication-kicker-badge">
                  <span className="communication-kicker-dot" />
                  <span>{commKicker}</span>
                </div>
              )}
              {commTitle && <h2 className="communication-title">{commTitle}</h2>}
              {commSubtitle && <p className="communication-subtitle" style={{ whiteSpace: 'pre-line' }}>{commSubtitle}</p>}
            </div>

            {/* Channels Grid */}
            {communicationChannels.length > 0 && (
              <div className="communication-channels-grid parents-animate">
                {communicationChannels.map((channel, cIdx) => {
                  const channelIcons = [
                    <GraduationCap size={28} />,
                    <Mail size={28} />,
                    <Layout size={28} />,
                    <MessageSquare size={28} />,
                  ];
                  const icon = channelIcons[cIdx % channelIcons.length];
                  const targetUrl = formatSafeUrl(channel.link_url, '#');
                  const isExternal = targetUrl.startsWith('http');
                  const isMailOrTel = targetUrl.startsWith('mailto:') || targetUrl.startsWith('tel:');

                  return (
                    <article key={channel.id || cIdx} className="communication-card">
                      <div className="communication-card-accent" />
                      <div className="channel-icon-wrap">{icon}</div>

                      <div className="communication-card-content">
                        <div className="channel-badge">Channel 0{cIdx + 1}</div>
                        <h3 className="channel-title">{channel.title}</h3>
                        <p className="channel-desc" style={{ whiteSpace: 'pre-line' }}>{channel.description}</p>

                        {Array.isArray(channel.features) && channel.features.length > 0 && (
                          <div className="channel-features-list">
                            {channel.features.map((feat, fIdx) => (
                              <div key={fIdx} className="channel-feature-item">
                                <CheckCircle2 size={16} />
                                <span>{feat}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="communication-card-footer">
                        <a
                          href={targetUrl}
                          className="communication-action-btn"
                          target={isMailOrTel ? '_self' : isExternal ? '_blank' : '_self'}
                          rel={isExternal ? 'noopener noreferrer' : undefined}
                        >
                          <span>{channel.link_label || 'Connect'}</span>
                          <ArrowRight size={16} />
                        </a>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}
    </section>
  );
}

export default ParentsPage;
